import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../packages/auth-client';
import { apiClient } from '../../packages/api-client';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, Table, StateView, Drawer, type Column } from '../../packages/ui';
import './platformOps.css';

interface AuditLog extends Record<string, unknown> {
    id: string;
    actor_id: string;
    actor_name?: string;
    action: string;
    entity: string;
    entity_id: string;
    entity_name?: string;
    metadata?: Record<string, unknown>;
    ip_address?: string;
    user_agent?: string;
    created_at: string;
}

export const AUDIT_PAGE_SIZE = 25;

// Filter values stay canonical snake_case for the API; only labels are humanized.
const ACTIONS = ['login', 'logout', 'create', 'update', 'delete', 'permission_change', 'impersonate', 'impersonate_exit'];
const FILTER_KEYS = ['action', 'entity', 'actor_id', 'from', 'to'] as const;

function humanize(s: string): string {
    return s ? s.split('_').map(w => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ') : s;
}

function actionTone(action: string): string {
    if (action === 'delete') return 'ops-pill ops-pill--danger';
    if (action.startsWith('impersonate') || action === 'permission_change') return 'ops-pill ops-pill--warn';
    if (action === 'create') return 'ops-pill ops-pill--ok';
    return 'ops-pill';
}

// Date inputs are local calendar days: "from" is the start of that day and
// "to" the end of it, so a one-day range includes the whole day.
function dayBound(day: string, end: boolean): string {
    return new Date(`${day}T${end ? '23:59:59.999' : '00:00:00'}`).toISOString();
}

const SECRET_KEY = /token|secret|password|key/i;
// Telegram bot tokens sit in the Bot API path; older send-failure events
// recorded that URL in their error text before the server stopped doing so.
const BOT_TOKEN_IN_URL = /\/bot\d+:[\w-]+/g;
const MASK = '••••••';

/** Defence in depth for the drawer: secret-looking keys and bot-token URLs never render. */
export function maskSecrets(value: unknown, key = ''): unknown {
    if (key && SECRET_KEY.test(key) && value !== null && typeof value !== 'object') return MASK;
    if (typeof value === 'string') return value.replace(BOT_TOKEN_IN_URL, `/bot${MASK}`);
    if (Array.isArray(value)) return value.map(v => maskSecrets(v));
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, maskSecrets(v, k)]));
    }
    return value;
}

type Load =
    | { kind: 'loading' }
    | { kind: 'error'; denied: boolean }
    | { kind: 'ok'; logs: AuditLog[]; total: number };

export function AuditLogPage() {
    usePageTitle('Audit log');
    const { isSuperAdmin } = useAuth();
    const [params, setParams] = useSearchParams();
    const [load, setLoad] = useState<Load>({ kind: 'loading' });
    const [reload, setReload] = useState(0);
    const [selected, setSelected] = useState<AuditLog | null>(null);

    const page = Math.max(1, Number(params.get('page')) || 1);
    const filters = Object.fromEntries(FILTER_KEYS.map(k => [k, params.get(k) ?? ''])) as Record<(typeof FILTER_KEYS)[number], string>;
    const hasFilters = FILTER_KEYS.some(k => filters[k]);
    const allowed = isSuperAdmin();
    const query = params.toString();

    useEffect(() => {
        if (!allowed) return;
        let live = true;
        const q = new URLSearchParams();
        if (filters.action) q.set('action', filters.action);
        if (filters.entity) q.set('entity', filters.entity);
        if (filters.actor_id) q.set('actor_id', filters.actor_id);
        if (filters.from) q.set('from', dayBound(filters.from, false));
        if (filters.to) q.set('to', dayBound(filters.to, true));
        q.set('limit', String(AUDIT_PAGE_SIZE));
        q.set('page', String(page));
        setLoad({ kind: 'loading' });
        apiClient.get<{ data: AuditLog[]; meta: { total: number } }>(`/admin/audit-logs?${q.toString()}`)
            .then(res => { if (live) setLoad({ kind: 'ok', logs: res.data.data ?? [], total: res.data.meta?.total ?? 0 }); })
            .catch(err => {
                if (live) setLoad({ kind: 'error', denied: axios.isAxiosError(err) && err.response?.status === 403 });
            });
        return () => { live = false; };
        // `query` captures every filter and the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed, query, reload]);

    const setFilter = useCallback((key: string, value: string) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value); else next.delete(key);
            next.delete('page');
            return next;
        }, { replace: true });
    }, [setParams]);

    const goToPage = (p: number) => setParams(prev => {
        const next = new URLSearchParams(prev);
        if (p > 1) next.set('page', String(p)); else next.delete('page');
        return next;
    });

    const clearFilters = () => setParams(new URLSearchParams(), { replace: true });

    if (!allowed || (load.kind === 'error' && load.denied)) {
        return (
            <div className="ops-page">
                <PageHeader title="Audit log" />
                <StateView state="denied" reason="The audit log is limited to platform superadmins."
                    nextStep="Ask a superadmin if you need an event looked up." />
            </div>
        );
    }

    const columns: Column<AuditLog>[] = [
        { key: 'created_at', header: 'Time', render: l => new Date(l.created_at).toLocaleString() },
        {
            key: 'actor', header: 'Actor', rowHeader: true,
            render: l => <>{l.actor_name || l.actor_id}{l.actor_name && <span className="ops-sub ops-mono">{l.actor_id}</span>}</>,
        },
        { key: 'action', header: 'Action', render: l => <span className={actionTone(l.action)}>{humanize(l.action)}</span> },
        {
            key: 'entity', header: 'Entity',
            render: l => <>{l.entity_name || <span className="ops-mono">{l.entity_id}</span>}<span className="ops-sub">{humanize(l.entity)}</span></>,
        },
        { key: 'client', header: 'Client', render: l => <span className="ops-mono">{l.ip_address || '—'}</span> },
        {
            key: 'inspect', header: 'Details',
            render: l => (
                <button type="button" className="btn btn-ghost btn-sm"
                    aria-label={`Inspect ${humanize(l.action)} event by ${l.actor_name || l.actor_id}`}
                    onClick={() => setSelected(l)}>
                    Inspect
                </button>
            ),
        },
    ];

    const total = load.kind === 'ok' ? load.total : 0;
    const totalPages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));

    return (
        <div className="ops-page">
            <PageHeader
                breadcrumb={[{ label: 'Platform' }, { label: 'Audit log' }]}
                title="Audit log"
                subtitle="Who changed what, with event metadata and request context."
            />
            <p className="ops-scope">
                Scope: platform-wide. Audit events do not record a tenant, so this log cannot be filtered by tenant.
            </p>

            <div className="ops-toolbar" role="search" aria-label="Filter audit events">
                <label className="ops-field">Action
                    <select className="form-input" value={filters.action} onChange={e => setFilter('action', e.target.value)}>
                        <option value="">All actions</option>
                        {ACTIONS.map(a => <option key={a} value={a}>{humanize(a)}</option>)}
                    </select>
                </label>
                <label className="ops-field">Entity
                    <input className="form-input" value={filters.entity} placeholder="e.g. task, user"
                        onChange={e => setFilter('entity', e.target.value.trim())} />
                </label>
                <label className="ops-field">Actor ID
                    <input className="form-input" value={filters.actor_id}
                        onChange={e => setFilter('actor_id', e.target.value.trim())} />
                </label>
                <label className="ops-field">From
                    <input className="form-input" type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} />
                </label>
                <label className="ops-field">To
                    <input className="form-input" type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} />
                </label>
                {hasFilters && (
                    <button type="button" className="btn btn-secondary btn-md" onClick={clearFilters}>Clear filters</button>
                )}
            </div>

            {load.kind === 'loading' && <StateView state="loading" label="Loading audit events" lines={6} />}
            {load.kind === 'error' && (
                <StateView state="error" title="Audit events couldn't be loaded"
                    message="The server did not return the audit log. Nothing below is current."
                    onRetry={() => setReload(n => n + 1)} />
            )}
            {load.kind === 'ok' && load.logs.length === 0 && (
                hasFilters || page > 1
                    ? <StateView state="filtered-empty" title="No events match these filters" onClearFilters={clearFilters} />
                    : <StateView state="empty" title="No audit events yet" message="Events appear here as people sign in and change records." />
            )}
            {load.kind === 'ok' && load.logs.length > 0 && (
                <>
                    <Table<AuditLog> caption="Audit events" hideCaption density="compact" columns={columns} data={load.logs} rowKey={l => l.id} />
                    <nav className="ops-pager" aria-label="Audit log pages">
                        <span>Page {page} of {totalPages} · {total.toLocaleString()} events</span>
                        <span className="ops-pager-buttons">
                            <button type="button" className="btn btn-secondary btn-md" disabled={page <= 1} onClick={() => goToPage(page - 1)}>Previous</button>
                            <button type="button" className="btn btn-secondary btn-md" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>Next</button>
                        </span>
                    </nav>
                </>
            )}

            <Drawer open={selected !== null} onClose={() => setSelected(null)}
                title={selected ? humanize(selected.action) : ''}>
                {selected && <AuditDetail log={selected} onFilterActor={id => { setSelected(null); setFilter('actor_id', id); }} />}
            </Drawer>
        </div>
    );
}

function AuditDetail({ log, onFilterActor }: { log: AuditLog; onFilterActor: (id: string) => void }) {
    const hasMeta = log.metadata && Object.keys(log.metadata).length > 0;
    return (
        <>
            <dl className="ops-detail">
                <dt>Time</dt><dd>{new Date(log.created_at).toLocaleString()}</dd>
                <dt>Actor</dt>
                <dd>
                    {log.actor_name || '—'}
                    <span className="ops-sub ops-mono">{log.actor_id}</span>
                </dd>
                <dt>Action</dt><dd>{humanize(log.action)} <span className="ops-sub ops-mono">{log.action}</span></dd>
                <dt>Entity</dt><dd>{humanize(log.entity)}{log.entity_name && ` · ${log.entity_name}`}</dd>
                <dt>Entity ID</dt><dd className="ops-mono">{log.entity_id || '—'}</dd>
            </dl>
            <section className="ops-section" aria-labelledby="audit-client">
                <h3 id="audit-client">Client context</h3>
                <dl className="ops-detail">
                    <dt>IP address</dt><dd className="ops-mono">{log.ip_address || 'Not recorded'}</dd>
                    <dt>User agent</dt><dd className="ops-mono">{log.user_agent || 'Not recorded'}</dd>
                </dl>
            </section>
            <section className="ops-section" aria-labelledby="audit-meta">
                <h3 id="audit-meta">Event metadata</h3>
                {hasMeta
                    ? <pre className="ops-pre">{JSON.stringify(maskSecrets(log.metadata), null, 2)}</pre>
                    : <p className="ops-scope">No metadata was recorded for this event.</p>}
            </section>
            {log.actor_id && (
                <button type="button" className="btn btn-secondary btn-md" onClick={() => onFilterActor(log.actor_id)}>
                    Show this actor's events
                </button>
            )}
        </>
    );
}
